
import { useEffect } from 'react';
import { useLocation, useNavigate, useParams, Link } from 'react-router-dom';
import { CheckCircle, Package, ArrowLeft, Truck, Download, ShoppingBag, MessageCircle, Calendar, Shield, Star, Gift } from 'lucide-react';
import { motion } from 'framer-motion';
import Header from '@/components/header/ModernHeader';
import Footer from '@/components/Footer';
import { useOrderConfirmation } from '@/hooks/useOrderConfirmation';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { formatPrice } from '@/utils/priceUtils';

interface OrderConfirmationState {
  orderId: string;
  orderNumber: string;
  customerName: string;
  customerEmail: string;
  totalAmount: number;
}

const OrderConfirmation = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams();
  
  const state = location.state as OrderConfirmationState;
  const orderIdFromParams = params.orderId;
  const orderId = state?.orderId || orderIdFromParams;

  const { order, isLoading, error } = useOrderConfirmation(orderId);

  useEffect(() => {
    if (!orderId) {
      console.log('No order ID found, redirecting to home');
      navigate('/');
    }
  }, [orderId, navigate]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-muted">
        <Header />
        <div className="container mx-auto px-4 py-16">
          <div className="max-w-2xl mx-auto text-center">
            <div className="floating-card bg-card p-8 rounded-modern">
              <div className="animate-spin rounded-full h-16 w-16 border-4 border-primary/20 border-t-primary mx-auto mb-6"></div>
              <p className="text-muted-foreground text-lg">Loading your order details...</p>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="min-h-screen bg-gradient-muted">
        <Header />
        <div className="container mx-auto px-4 py-16">
          <div className="max-w-2xl mx-auto text-center">
            <div className="floating-card bg-card p-10 rounded-modern">
              <Package className="h-20 w-20 text-muted-foreground mx-auto mb-6" />
              <h1 className="text-3xl font-bold font-playfair mb-4">Order Not Found</h1>
              <p className="text-muted-foreground mb-8 text-lg">
                We couldn't find the order you're looking for.
              </p>
              <Button asChild size="lg" className="bg-gradient-primary text-white hover:opacity-90">
                <Link to="/">Return Home</Link>
              </Button>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const orderNumber = order.order_number || order.id.slice(0, 8);
  const customerName = order.profiles?.full_name || order.customer_name;

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-muted/50 to-accent/10">
      <Header />
      
      {/* Hero Section with Celebration */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-primary/10 via-transparent to-accent/10" />
        <div className="container mx-auto px-4 py-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center max-w-3xl mx-auto"
          >
            {/* Success Icon */}
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: "spring" }}
              className="inline-flex items-center justify-center w-24 h-24 mb-8 bg-gradient-to-r from-green-500 to-emerald-600 rounded-full shadow-2xl"
            >
              <CheckCircle className="w-12 h-12 text-white" />
            </motion.div>

            {/* Main Title */}
            <motion.h1
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
              className="text-5xl font-bold bg-gradient-to-r from-primary via-accent to-primary bg-clip-text text-transparent mb-4"
            >
              Order Confirmed!
            </motion.h1>

            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4 }}
              className="text-xl text-muted-foreground mb-6"
            >
              {customerName ? `Thank you, ${customerName}!` : 'Thank you for your order!'} 
              We've received your order and will process it shortly.
            </motion.p>

            {/* Order Number Badge */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.5 }}
              className="inline-flex items-center gap-2 px-6 py-3 bg-card border-2 border-primary/20 rounded-full shadow-lg"
            >
              <Package className="w-5 h-5 text-primary" />
              <span className="font-semibold">Order #{orderNumber}</span>
            </motion.div>
          </motion.div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 pb-16">
        <div className="max-w-6xl mx-auto">
          {/* Progress Steps */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="mb-12"
          >
            <Card className="p-6 bg-gradient-to-r from-card to-muted/20 border-0 shadow-xl">
              <div className="flex items-center justify-between relative">
                <div className="absolute top-6 left-8 right-8 h-0.5 bg-gradient-to-r from-primary to-accent"></div>
                {['Order Placed', 'Processing', 'Shipped', 'Delivered'].map((step, index) => (
                  <div key={step} className="flex flex-col items-center relative z-10">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                      index === 0 ? 'bg-primary text-white' : 
                      index === 1 ? 'bg-accent text-white' : 
                      'bg-muted text-muted-foreground'
                    }`}>
                      {index === 0 && <CheckCircle className="w-6 h-6" />}
                      {index === 1 && <Package className="w-6 h-6" />}
                      {index === 2 && <Truck className="w-6 h-6" />}
                      {index === 3 && <Gift className="w-6 h-6" />}
                    </div>
                    <span className="text-sm font-medium mt-2">{step}</span>
                  </div>
                ))}
              </div>
            </Card>
          </motion.div>

          {/* Main Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Order Summary - Left Column */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.7 }}
              className="lg:col-span-2 space-y-6"
            >
              {/* Order Items */}
              <Card className="p-6 shadow-xl border-0 bg-gradient-to-br from-card to-muted/10">
                <h3 className="text-xl font-semibold mb-6 flex items-center gap-2">
                  <ShoppingBag className="w-5 h-5 text-primary" />
                  Order Items
                </h3>
                <div className="space-y-4">
                  {order.order_items?.map((item) => (
                    <div key={item.id} className="flex items-center gap-4 p-4 bg-background/50 rounded-lg border">
                      <div className="w-16 h-16 bg-muted rounded-lg flex items-center justify-center">
                        <Package className="w-8 h-8 text-muted-foreground" />
                      </div>
                      <div className="flex-1">
                        <h4 className="font-medium">{item.products?.name || 'Product'}</h4>
                        <p className="text-sm text-muted-foreground">Quantity: {item.quantity}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold">{formatPrice(item.price * item.quantity)}</p>
                      </div>
                    </div>
                  ))}
                </div>
                
                <Separator className="my-6" />
                
                {/* Total */}
                <div className="flex justify-between items-center text-lg font-bold">
                  <span>Total</span>
                  <span className="text-primary">{formatPrice(order.total_amount)}</span>
                </div>
              </Card>

              {/* Shipping & Payment Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Card className="p-6 shadow-xl border-0 bg-gradient-to-br from-card to-muted/10">
                  <h3 className="font-semibold mb-4 flex items-center gap-2">
                    <Truck className="w-5 h-5 text-primary" />
                    Shipping Address
                  </h3>
                  <div className="text-sm text-muted-foreground space-y-1">
                    {order.shipping_address ? (
                      <>
                        <p>{order.shipping_address.line1}</p>
                        {order.shipping_address.line2 && <p>{order.shipping_address.line2}</p>}
                        <p>{order.shipping_address.city}, {order.shipping_address.state} {order.shipping_address.postal_code}</p>
                        <p>{order.shipping_address.country}</p>
                      </>
                    ) : (
                      <p>No shipping address provided</p>
                    )}
                  </div>
                </Card>

                <Card className="p-6 shadow-xl border-0 bg-gradient-to-br from-card to-muted/10">
                  <h3 className="font-semibold mb-4 flex items-center gap-2">
                    <Shield className="w-5 h-5 text-primary" />
                    Payment Method
                  </h3>
                  <div className="space-y-2">
                    <p className="text-sm">{order.payment_method || 'Card'}</p>
                    <Badge variant={order.payment_status === 'paid' ? 'default' : 'secondary'}>
                      {order.payment_status || 'Pending'}
                    </Badge>
                  </div>
                </Card>
              </div>
            </motion.div>

            {/* Actions & Info - Right Column */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.8 }}
              className="space-y-6"
            >
              {/* Quick Actions */}
              <Card className="p-6 shadow-xl border-0 bg-gradient-to-br from-card to-muted/10">
                <h3 className="font-semibold mb-4">Quick Actions</h3>
                <div className="space-y-3">
                  <Button asChild className="w-full justify-start" variant="outline">
                    <Link to="/orders">
                      <Package className="w-4 h-4 mr-2" />
                      Track Order
                    </Link>
                  </Button>
                  <Button className="w-full justify-start" variant="outline">
                    <Download className="w-4 h-4 mr-2" />
                    Download Receipt
                  </Button>
                  <Button asChild className="w-full justify-start" variant="outline">
                    <Link to="/contact">
                      <MessageCircle className="w-4 h-4 mr-2" />
                      Contact Support
                    </Link>
                  </Button>
                </div>
              </Card>

              {/* Order Details */}
              <Card className="p-6 shadow-xl border-0 bg-gradient-to-br from-card to-muted/10">
                <h3 className="font-semibold mb-4">Order Details</h3>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Order Date:</span>
                    <span>{new Date(order.created_at).toLocaleDateString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Status:</span>
                    <Badge>{order.status}</Badge>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Expected Delivery:</span>
                    <span className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      3-5 days
                    </span>
                  </div>
                </div>
              </Card>

              {/* Continue Shopping */}
              <Card className="p-6 shadow-xl border-0 bg-gradient-to-br from-primary/10 to-accent/10">
                <h3 className="font-semibold mb-4">What's Next?</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Keep exploring our amazing products while we prepare your order.
                </p>
                <Button asChild className="w-full" size="lg">
                  <Link to="/products">
                    <ShoppingBag className="w-4 h-4 mr-2" />
                    Continue Shopping
                  </Link>
                </Button>
              </Card>

              {/* Trust Elements */}
              <Card className="p-6 shadow-xl border-0 bg-gradient-to-br from-card to-muted/10">
                <div className="text-center">
                  <Shield className="w-8 h-8 text-primary mx-auto mb-2" />
                  <h4 className="font-medium mb-1">Secure Purchase</h4>
                  <p className="text-xs text-muted-foreground">
                    Your order is protected by our secure payment system
                  </p>
                </div>
              </Card>
            </motion.div>
          </div>
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default OrderConfirmation;
